import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  X, ChevronLeft, Loader2, RefreshCw, AlertTriangle, CheckCircle2,
  Globe, Link as LinkIcon, ExternalLink, ShieldAlert, Smartphone, Monitor,
  Send, Eye, FileSearch, Grid3x3, Lock,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminDossierPreview, adminDossierSourceHealth, adminDossierMatrix,
  adminDossierSetClaimSupport, adminDossierPreviewAck,
  adminDossierPublishGate, adminDossierPublish,
} from "../../lib/api";

/**
 * PrePublishReview — modal-style sheet that gates `Publish` for a dossier.
 *
 * Three tabs:
 *   1. PREVIEW   — public-shape render of the dossier (mobile/desktop toggle)
 *   2. SOURCES   — URL health check per source
 *   3. MATRIX    — claim-by-claim evidence matrix with support ratings
 *
 * Sticky bottom bar shows the publish-gate verdict and the Publish action.
 * The Publish button is disabled until the preview has been viewed at least
 * once. Backend enforces every constraint independently.
 *
 * Used for dtype="topic" only in v1.
 */

const TAB_META = {
  preview:  { label: "Preview Public Post",   icon: Eye,        testid: "pp-tab-preview" },
  sources:  { label: "Check Sources",         icon: FileSearch, testid: "pp-tab-sources" },
  matrix:   { label: "Review Evidence Matrix", icon: Grid3x3,    testid: "pp-tab-matrix"  },
};

const STATUS_META = {
  ok:           { label: "URL OK",        color: "#5DD4B3" },
  redirected:   { label: "REDIRECTED",    color: "#E0A526" },
  not_found:    { label: "404 BROKEN",    color: "#D92D20" },
  server_error: { label: "5xx ERROR",     color: "#D92D20" },
  paywalled:    { label: "PAYWALLED",     color: "#E0A526" },
  timeout:      { label: "TIMEOUT",       color: "#E0A526" },
  unreachable:  { label: "UNREACHABLE",   color: "#D92D20" },
  missing:      { label: "MISSING URL",   color: "#D92D20" },
  uncertain:    { label: "UNCERTAIN",     color: "#E0A526" },
};

const RATING_META = {
  DIRECT_SUPPORT:    { label: "DIRECT SUPPORT",    color: "#5DD4B3" },
  PARTIAL_SUPPORT:   { label: "PARTIAL SUPPORT",   color: "#9FA8D4" },
  BACKGROUND_ONLY:   { label: "BACKGROUND ONLY",   color: "#CBB892" },
  DOES_NOT_SUPPORT:  { label: "DOES NOT SUPPORT",  color: "#D92D20" },
  BROKEN_UNVERIFIED: { label: "BROKEN / UNVERIFIED", color: "#E0A526" },
};

const CONFIDENCE_META = {
  STRONG:               { label: "Strong source base",      color: "#5DD4B3" },
  MODERATE:             { label: "Moderate source base",    color: "#9FA8D4" },
  WEAK:                 { label: "Weak — verify",           color: "#E0A526" },
  UNCERTAIN:            { label: "Uncertain — fix sources", color: "#D92D20" },
  NEEDS_PRIMARY_SOURCE: { label: "Needs primary source",    color: "#E0A526" },
};

const MISSING_HINTS = {
  primary_document: "Primary document",
  court_filing: "Court filing",
  government_record: "Government record",
  archive_record: "Archive record",
  direct_quote: "Direct quote",
  document_exhibit: "Email / document exhibit",
  witness_testimony: "Witness testimony",
  financial_record: "Financial record",
  flight_log: "Flight log",
  property_record: "Property record",
};

export const PrePublishReview = ({
  dossierType = "topic",
  dossierId,
  slug,
  title,
  token,
  userToken = null,
  onClose = () => {},
  onPublished = () => {},
}) => {
  const [tab, setTab] = useState("preview");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [viewport, setViewport] = useState("desktop");

  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const [matrix, setMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);

  const [gate, setGate] = useState(null);
  const [gateLoading, setGateLoading] = useState(false);
  const [previewAcked, setPreviewAcked] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);

  // Load preview on mount
  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const p = await adminDossierPreview(token, dossierType, dossierId, userToken);
      setPreview(p);
      setPreviewAcked(!!p.pre_publish_state?.preview_acked_at);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  }, [token, userToken, dossierType, dossierId]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  // Auto-ack the preview once it's loaded and the user opens the modal.
  // This is the "preview reviewed" precondition. Backend persists the ack.
  useEffect(() => {
    if (previewLoading || previewAcked || !preview) return;
    (async () => {
      try {
        await adminDossierPreviewAck(token, dossierType, dossierId, userToken);
        setPreviewAcked(true);
      } catch {
        // non-fatal — admin can still click Publish; backend will block.
      }
    })();
  }, [previewLoading, previewAcked, preview, token, userToken, dossierType, dossierId]);

  const runSourceHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const r = await adminDossierSourceHealth(token, dossierType, dossierId, userToken);
      setHealth(r);
      toast.success(`Checked ${r.summary.total} source URL${r.summary.total === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Source health check failed");
    } finally {
      setHealthLoading(false);
    }
  }, [token, userToken, dossierType, dossierId]);

  const loadMatrix = useCallback(async () => {
    setMatrixLoading(true);
    try {
      const m = await adminDossierMatrix(token, dossierType, dossierId, userToken);
      setMatrix(m);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load evidence matrix");
    } finally {
      setMatrixLoading(false);
    }
  }, [token, userToken, dossierType, dossierId]);

  useEffect(() => {
    if (tab === "matrix" && !matrix) loadMatrix();
  }, [tab, matrix, loadMatrix]);

  const runGate = useCallback(async () => {
    setGateLoading(true);
    try {
      const g = await adminDossierPublishGate(token, dossierType, dossierId, userToken);
      setGate(g);
      return g;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish-gate check failed");
      return null;
    } finally {
      setGateLoading(false);
    }
  }, [token, userToken, dossierType, dossierId]);

  // Refresh the gate whenever the underlying inputs change
  useEffect(() => {
    if (!previewLoading && previewAcked) runGate();
  }, [previewLoading, previewAcked, health, matrix, runGate]);

  const onPublishClick = async () => {
    const g = await runGate();
    if (!g) return;
    if (!g.ok) {
      setShowOverrideModal(true);
      return;
    }
    setPublishing(true);
    try {
      await adminDossierPublish(token, dossierType, dossierId, {}, userToken);
      toast.success("Dossier published");
      onPublished();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const onOverrideSubmit = async (reason) => {
    setPublishing(true);
    try {
      await adminDossierPublish(token, dossierType, dossierId,
        { override: true, override_reason: reason }, userToken);
      toast.success("Dossier published with override");
      setShowOverrideModal(false);
      onPublished();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/80 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      data-testid="pre-publish-review"
    >
      <div className="w-full max-w-5xl h-full bg-[#0a0907] border border-[#2D2A26] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-[#2D2A26] bg-[#0F0F0F] px-4 py-3 shrink-0">
          <button type="button" onClick={onClose} data-testid="pp-close"
                  className="inline-flex items-center justify-center w-7 h-7 text-[#A39E93] hover:text-[#D92D20]">
            <ChevronLeft size={14} />
          </button>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
              // PRE-PUBLISH REVIEW
            </div>
            <div className="font-display text-lg text-[#E4DFD3] truncate max-w-[600px]">
              {title || slug}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="ml-auto inline-flex items-center justify-center w-7 h-7 text-[#A39E93] hover:text-[#D92D20]">
            <X size={13} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-[#2D2A26] bg-[#0F0F0F] px-2 pt-2 shrink-0">
          {Object.entries(TAB_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const active = tab === key;
            return (
              <button
                key={key}
                onClick={() => setTab(key)}
                data-testid={meta.testid}
                className={
                  "px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 inline-flex items-center gap-1.5 transition-colors " +
                  (active
                    ? "border-[#D92D20] text-[#E4DFD3] bg-[#1a0a0a]"
                    : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")
                }
              >
                <Icon size={11} /> {meta.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {tab === "preview" && (
            <PreviewTab preview={preview} loading={previewLoading} viewport={viewport} setViewport={setViewport} />
          )}
          {tab === "sources" && (
            <SourcesTab health={health} loading={healthLoading} onCheck={runSourceHealth} preview={preview} />
          )}
          {tab === "matrix" && (
            <MatrixTab
              matrix={matrix}
              loading={matrixLoading}
              preview={preview}
              token={token}
              userToken={userToken}
              dossierType={dossierType}
              dossierId={dossierId}
              onChanged={loadMatrix}
            />
          )}
        </div>

        {/* Sticky footer: gate status + Publish button */}
        <GateFooter
          gate={gate}
          gateLoading={gateLoading}
          previewLoading={previewLoading}
          previewAcked={previewAcked}
          publishing={publishing}
          onPublish={onPublishClick}
        />

        {showOverrideModal && (
          <OverrideModal
            gate={gate}
            onClose={() => setShowOverrideModal(false)}
            onSubmit={onOverrideSubmit}
            busy={publishing}
          />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// PREVIEW tab
// ---------------------------------------------------------------------
const PreviewTab = ({ preview, loading, viewport, setViewport }) => {
  if (loading || !preview) {
    return <div className="font-mono text-xs text-[#757065]">// LOADING PREVIEW…</div>;
  }
  const mobileWidth = "max-w-[400px]";
  const desktopWidth = "max-w-full";
  return (
    <div data-testid="pp-preview-panel">
      <div className="flex items-center gap-2 mb-3">
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">
          Viewport
        </span>
        <button
          onClick={() => setViewport("desktop")}
          data-testid="pp-viewport-desktop"
          className={"inline-flex items-center gap-1 px-2 py-1 font-mono text-[10px] uppercase tracking-widest border " +
            (viewport === "desktop"
              ? "border-[#D92D20] text-[#D92D20]"
              : "border-[#454038] text-[#A39E93] hover:border-[#D92D20]")}
        >
          <Monitor size={10} /> Desktop
        </button>
        <button
          onClick={() => setViewport("mobile")}
          data-testid="pp-viewport-mobile"
          className={"inline-flex items-center gap-1 px-2 py-1 font-mono text-[10px] uppercase tracking-widest border " +
            (viewport === "mobile"
              ? "border-[#D92D20] text-[#D92D20]"
              : "border-[#454038] text-[#A39E93] hover:border-[#D92D20]")}
        >
          <Smartphone size={10} /> Mobile
        </button>
        <a
          href={preview.public_url}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#A39E93] hover:text-[#E4DFD3]"
        >
          <ExternalLink size={10} /> Open public route
        </a>
      </div>

      <div className={`mx-auto ${viewport === "mobile" ? mobileWidth : desktopWidth} border border-[#2D2A26] bg-[#0F0F0F] p-4`}>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-1">
          // {preview.category || "DOSSIER"}
        </div>
        <h1
          className="font-display text-3xl sm:text-4xl text-[#E4DFD3] mb-2 leading-tight"
          data-testid="pp-preview-title"
        >
          {preview.title}
        </h1>
        {preview.summary && (
          <p className="text-[#CBB892] mb-4" data-testid="pp-preview-summary">{preview.summary}</p>
        )}
        <div className="flex flex-wrap gap-2 mb-4">
          {(preview.tags || []).slice(0, 6).map((t) => (
            <span key={t} className="font-mono text-[9px] uppercase tracking-[0.25em] border border-[#454038] text-[#A39E93] px-2 py-0.5">
              {t}
            </span>
          ))}
        </div>

        <PreviewSection title="Hidden truth" body={preview.hidden_truth} />
        <PreviewKeyPlayers players={preview.key_players} />
        <PreviewSourceCards sources={preview.sources} health={preview.editorial_modules?.source_health} />
        <PreviewTimeline events={preview.timeline} />
        <PreviewEvidence cards={preview.evidence} />
        <PreviewUnresolved questions={preview.unresolved} />
        <PreviewFooter footer={preview.footer} />
      </div>
    </div>
  );
};

const PreviewSection = ({ title, body }) => {
  if (!body) return null;
  return (
    <div className="mb-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">
        // {title}
      </div>
      <p className="text-[#E4DFD3] leading-relaxed text-sm">{body}</p>
    </div>
  );
};

const PreviewKeyPlayers = ({ players }) => {
  if (!players || players.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">// Key players</div>
      <div className="flex flex-wrap gap-1.5">
        {players.map((p, i) => (
          <span key={i} className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#CBB892] text-[#CBB892] px-2 py-0.5">
            {typeof p === "string" ? p : (p.name || p.label || JSON.stringify(p))}
          </span>
        ))}
      </div>
    </div>
  );
};

const PreviewSourceCards = ({ sources, health }) => {
  if (!sources || sources.length === 0) return null;
  const hMap = Object.fromEntries((health || []).map((h) => [h.idx, h]));
  return (
    <div className="mb-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">// Sources · {sources.length}</div>
      <div className="space-y-1.5">
        {sources.map((s, i) => {
          const h = hMap[i];
          const meta = h ? STATUS_META[h.status] : null;
          return (
            <div key={i} className="border border-[#2D2A26] p-2">
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[9px] tracking-[0.3em] text-[#757065]">#{i}</span>
                {s.primary && <span className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/50 px-1">PRIMARY</span>}
                {meta && (
                  <span style={{ color: meta.color, borderColor: meta.color }}
                        className="font-mono text-[8.5px] uppercase tracking-[0.25em] border px-1">
                    {meta.label}
                  </span>
                )}
                {s.reliability && s.reliability !== "NR" && (
                  <span className="font-mono text-[8.5px] uppercase tracking-[0.25em] text-[#A39E93]">{s.reliability}</span>
                )}
              </div>
              <div className="font-mono text-[11px] text-[#E4DFD3] truncate mt-0.5">{s.title}</div>
              <div className="font-mono text-[10px] text-[#757065] truncate">{s.publisher}</div>
              <a href={s.url} target="_blank" rel="noopener noreferrer"
                 className="font-mono text-[10px] text-[#9FA8D4] hover:text-[#E4DFD3] truncate inline-flex items-center gap-1">
                <LinkIcon size={9} /> {s.url}
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PreviewTimeline = ({ events }) => {
  if (!events || events.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">// Timeline · {events.length}</div>
      <div className="space-y-1.5">
        {events.map((e, i) => (
          <div key={i} className="border-l-2 border-[#454038] pl-3 py-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">{e.date}</div>
            <div className="font-mono text-[11px] text-[#E4DFD3]">{e.headline || e.event}</div>
            {e.body && <div className="font-mono text-[10px] text-[#757065] mt-0.5">{e.body}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

const PreviewEvidence = ({ cards }) => {
  if (!cards || cards.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">// Evidence · {cards.length}</div>
      <div className="space-y-1.5">
        {cards.map((c, i) => (
          <div key={i} className="border border-[#2D2A26] p-2">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#CBB892]">{c.kind || "document"}</div>
            <div className="font-mono text-[11px] text-[#E4DFD3] mt-0.5">{c.title}</div>
            <div className="font-mono text-[10px] text-[#A39E93] mt-0.5">{c.summary}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PreviewUnresolved = ({ questions }) => {
  if (!questions || questions.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">// Unresolved</div>
      <ul className="space-y-1 list-disc list-inside font-mono text-[11px] text-[#CBB892]">
        {questions.map((q, i) => (
          <li key={i}>{typeof q === "string" ? q : q.question}</li>
        ))}
      </ul>
    </div>
  );
};

const PreviewFooter = ({ footer }) => {
  if (!footer) return null;
  return (
    <div className="border-t border-[#2D2A26] pt-3 mt-4 font-mono text-[9.5px] text-[#5a544a] leading-relaxed">
      {footer}
    </div>
  );
};

// ---------------------------------------------------------------------
// SOURCES tab
// ---------------------------------------------------------------------
const SourcesTab = ({ health, loading, onCheck, preview }) => {
  const lastRun = preview?.editorial_modules?.source_health_last_run_at;
  // If health hasn't been fetched in this session, fall back to what's stored
  // in the preview's source_health array.
  const rows = health?.health || preview?.editorial_modules?.source_health || [];
  const summary = health?.summary;
  return (
    <div data-testid="pp-sources-panel">
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={onCheck}
          disabled={loading}
          data-testid="pp-run-source-check"
          className="inline-flex items-center gap-1.5 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
        >
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          {loading ? "Checking…" : "Re-check Sources"}
        </button>
        {summary && (
          <div className="font-mono text-[10px] text-[#A39E93]">
            <span className="text-[#5DD4B3]">{summary.ok || 0} OK</span> · {" "}
            <span className="text-[#E0A526]">{(summary.redirected || 0) + (summary.paywalled || 0) + (summary.timeout || 0)} attention</span> · {" "}
            <span className="text-[#D92D20]">{(summary.not_found || 0) + (summary.unreachable || 0) + (summary.missing || 0)} broken</span>
            {lastRun && <span className="text-[#5a544a]"> · last: {lastRun.slice(0, 16).replace("T", " ")}</span>}
          </div>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="border border-[#2D2A26] p-6 text-center font-mono text-xs text-[#757065]">
          Run Source URL Health Check to populate this list.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((h, i) => {
            const meta = STATUS_META[h.status] || STATUS_META.uncertain;
            return (
              <div
                key={h.idx ?? i}
                data-testid={`pp-source-row-${h.idx ?? i}`}
                className="border p-3"
                style={{ borderColor: h.status === "ok" ? "#2D2A26" : `${meta.color}55` }}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[9px] tracking-[0.3em] text-[#757065]">#{h.idx ?? i}</span>
                  <span style={{ color: meta.color, borderColor: meta.color }}
                        className="font-mono text-[9px] uppercase tracking-[0.3em] border px-2 py-0.5"
                        data-testid={`pp-source-status-${h.idx ?? i}`}>
                    {meta.label}
                  </span>
                  {h.primary && <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/50 px-2 py-0.5">PRIMARY</span>}
                  {h.http_code != null && (
                    <span className="font-mono text-[9px] text-[#757065]">{h.http_code}</span>
                  )}
                  {h.redirected && h.final_url && (
                    <span className="font-mono text-[9px] text-[#E0A526] truncate ml-auto">→ {h.final_url}</span>
                  )}
                </div>
                <div className="font-mono text-[11px] text-[#E4DFD3] mt-1">{h.title || "(untitled)"}</div>
                <div className="font-mono text-[10px] text-[#A39E93]">{h.publisher || ""}</div>
                <a href={h.url} target="_blank" rel="noopener noreferrer"
                   className="font-mono text-[10px] text-[#9FA8D4] hover:text-[#E4DFD3] inline-flex items-center gap-1 break-all">
                  <LinkIcon size={9} /> {h.url || "(no URL)"}
                </a>
                {h.note && (
                  <div className="font-mono text-[10px] text-[#CBB892] mt-1">{h.note}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// MATRIX tab
// ---------------------------------------------------------------------
const MatrixTab = ({ matrix, loading, preview, token, userToken, dossierType, dossierId, onChanged }) => {
  const [savingKey, setSavingKey] = useState(null);

  const sources = preview?.sources || [];

  const setRating = async (claim_id, source_index, rating) => {
    setSavingKey(`${claim_id}:${source_index}`);
    try {
      await adminDossierSetClaimSupport(token, dossierType, dossierId,
        { claim_id, source_index, rating }, userToken);
      toast.success("Rating saved");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  if (loading || !matrix) {
    return <div className="font-mono text-xs text-[#757065]">// LOADING MATRIX…</div>;
  }
  if (matrix.summary.claims_total === 0) {
    return (
      <div className="border border-[#2D2A26] p-6 text-center" data-testid="pp-matrix-empty">
        <Grid3x3 size={20} className="text-[#5a544a] mx-auto mb-2" />
        <div className="font-mono text-xs text-[#A39E93]">
          No timeline events or evidence cards in `editorial_modules` yet.
        </div>
        <div className="font-mono text-[10px] text-[#5a544a] mt-1">
          Once you add timeline/evidence (each with source_indices), the matrix
          will surface claim-by-claim confidence and missing evidence here.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pp-matrix-panel">
      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <StatCard label="Strong"    count={matrix.summary.claims_strong}    color="#5DD4B3" testid="pp-matrix-strong" />
        <StatCard label="Moderate"  count={matrix.summary.claims_moderate}  color="#9FA8D4" testid="pp-matrix-moderate" />
        <StatCard label="Weak"      count={matrix.summary.claims_weak + matrix.summary.claims_needs_primary} color="#E0A526" testid="pp-matrix-weak" />
        <StatCard label="Uncertain" count={matrix.summary.claims_uncertain} color="#D92D20" testid="pp-matrix-uncertain" />
      </div>

      <div className="space-y-3">
        {matrix.rows.map((r) => {
          const confMeta = CONFIDENCE_META[r.confidence] || CONFIDENCE_META.UNCERTAIN;
          return (
            <div
              key={r.claim_id}
              data-testid={`pp-matrix-row-${r.claim_id}`}
              className="border p-3"
              style={{ borderColor: r.confidence === "STRONG" ? "#2D2A26" : `${confMeta.color}55` }}
            >
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">
                  {r.kind === "timeline" ? "TIMELINE" : "EVIDENCE"} · {r.claim_id}
                </span>
                <span style={{ color: confMeta.color, borderColor: confMeta.color }}
                      className="font-mono text-[9px] uppercase tracking-[0.3em] border px-2 py-0.5">
                  {confMeta.label}
                </span>
                <span className="ml-auto font-mono text-[9px] text-[#A39E93]">
                  {r.primary_count}P / {r.secondary_count}S — {r.source_count} total
                </span>
              </div>
              <div className="font-mono text-[12px] text-[#E4DFD3] mb-2">{r.text}</div>
              {r.missing_evidence && (
                <div className="font-mono text-[10px] text-[#E0A526] mb-2 inline-flex items-center gap-1">
                  <AlertTriangle size={10} /> Evidence needed: {MISSING_HINTS[r.missing_evidence] || r.missing_evidence}
                </div>
              )}

              {/* Attached sources × ratings */}
              <div className="space-y-1.5">
                {(r.attached_source_indices || []).length === 0 && (
                  <div className="font-mono text-[10px] text-[#757065]">
                    No sources attached. Edit the dossier to link sources to this claim.
                  </div>
                )}
                {(r.attached_source_indices || []).map((sIdx, i) => {
                  const src = sources[sIdx];
                  const currentRating = r.ratings?.[i];
                  const key = `${r.claim_id}:${sIdx}`;
                  return (
                    <div key={sIdx} className="bg-[#0F0F0F] border border-[#2D2A26] p-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[9px] tracking-[0.3em] text-[#757065]">#{sIdx}</span>
                        {src?.primary && <span className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/50 px-1">PRIMARY</span>}
                        <span className="font-mono text-[10px] text-[#E4DFD3] truncate flex-1">{src?.title || "(untitled)"}</span>
                      </div>
                      <div className="font-mono text-[9.5px] text-[#757065] truncate">{src?.publisher}</div>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {Object.entries(RATING_META).map(([rk, rmeta]) => {
                          const active = currentRating === rk;
                          return (
                            <button
                              key={rk}
                              type="button"
                              onClick={() => setRating(r.claim_id, sIdx, rk)}
                              disabled={savingKey === key}
                              data-testid={`pp-rating-${r.claim_id}-${sIdx}-${rk}`}
                              style={active ? { borderColor: rmeta.color, color: rmeta.color, backgroundColor: `${rmeta.color}15` } : {}}
                              className={
                                "px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.25em] border " +
                                (active ? "" : "border-[#454038] text-[#A39E93] hover:text-[#E4DFD3]")
                              }
                            >
                              {rmeta.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatCard = ({ label, count, color, testid }) => (
  <div
    data-testid={testid}
    className="border p-3"
    style={{ borderColor: `${color}55`, backgroundColor: `${color}08` }}
  >
    <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">{label}</div>
    <div className="font-display text-2xl mt-1" style={{ color }}>{count ?? 0}</div>
  </div>
);

// ---------------------------------------------------------------------
// Sticky footer + Override modal
// ---------------------------------------------------------------------
const GateFooter = ({ gate, gateLoading, previewLoading, previewAcked, publishing, onPublish }) => {
  const canPublish = previewAcked && !previewLoading && !publishing;
  const blockers = gate?.blockers || [];
  const warnings = gate?.warnings || [];
  const ok = gate?.ok;
  return (
    <div
      className="sticky bottom-0 border-t border-[#2D2A26] bg-[#0F0F0F] px-4 py-3 flex items-center gap-3"
      data-testid="pp-gate-footer"
    >
      <div className="flex-1 min-w-0">
        {gateLoading ? (
          <span className="font-mono text-[10px] text-[#757065]">Evaluating publish gate…</span>
        ) : ok ? (
          <span className="font-mono text-[10px] text-[#5DD4B3] inline-flex items-center gap-1.5"
                data-testid="pp-gate-ok">
            <CheckCircle2 size={11} /> Pre-publish checks pass · {warnings.length} warning{warnings.length === 1 ? "" : "s"}
          </span>
        ) : (
          <span className="font-mono text-[10px] text-[#D92D20] inline-flex items-center gap-1.5"
                data-testid="pp-gate-blocked">
            <AlertTriangle size={11} /> {blockers.length} blocker{blockers.length === 1 ? "" : "s"} · {warnings.length} warning{warnings.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onPublish}
        disabled={!canPublish}
        data-testid={ok ? "pp-publish" : "pp-publish-blocked"}
        className={
          "inline-flex items-center gap-1.5 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.3em] " +
          (canPublish && ok
            ? "bg-[#5DD4B3] hover:bg-[#4ec0a0] text-[#0F0F0F]"
            : canPublish
              ? "bg-[#D92D20] hover:bg-[#B92318] text-white"
              : "bg-[#2D2A26] text-[#5a544a] cursor-not-allowed")
        }
      >
        {publishing ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
        {ok ? "Publish" : "Publish Anyway With Reason"}
      </button>
    </div>
  );
};

const OverrideModal = ({ gate, onClose, onSubmit, busy }) => {
  const [reason, setReason] = useState("");
  const canSubmit = reason.trim().length >= 8 && !busy;
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
         role="dialog" aria-modal="true" data-testid="pp-override-modal">
      <div className="w-full max-w-lg bg-[#0a0907] border border-[#D92D20] p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert size={14} className="text-[#D92D20]" />
          <h3 className="font-display text-xl text-[#E4DFD3]">Publish Anyway · Override Required</h3>
        </div>
        <div className="font-mono text-[10px] text-[#A39E93] mb-3">
          The publish gate found {gate?.blockers?.length || 0} blocker(s). Override is audit-logged
          with your reason. The dossier will publish despite the blockers.
        </div>
        <ul className="mb-3 space-y-1 list-disc list-inside font-mono text-[10px] text-[#CBB892]">
          {(gate?.blockers || []).slice(0, 6).map((b, i) => (
            <li key={i}>{b.message || b.code}</li>
          ))}
        </ul>
        <label className="block mb-3">
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">
            Override reason (≥ 8 chars, audit-logged)
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={2}
            data-testid="pp-override-reason"
            placeholder="e.g. paywalled NYT URL is canonical, replaced when we get archive.org snapshot"
            className="mt-1 w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-2 py-1.5 font-mono text-[11px] text-[#E4DFD3] placeholder:text-[#5a544a]"
          />
        </label>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
                  data-testid="pp-override-cancel"
                  className="border border-[#454038] hover:border-[#E4DFD3] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={() => onSubmit(reason.trim())} disabled={!canSubmit}
                  data-testid="pp-override-submit"
                  className="inline-flex items-center gap-1.5 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] disabled:opacity-40">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            Publish With Override
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrePublishReview;
